import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Map as YandexMap,
    Placemark,
    YMaps,
    ZoomControl,
} from "react-yandex-maps";

import {
    prefersReducedMotion,
} from "../../../../../presentation/public-site/motion-preference";
import {
    createDecorativeFleet,
    getDecorativeVehiclePosition,
} from "./decorative-fleet";
import {
    createYandexMapsQuery,
} from "../../../../../presentation/public-site/yandex-maps.config";
import DecorativeFleetVehicleCard from "./DecorativeFleetVehicleCard";

const FRAME_INTERVAL_MS = 50;
const MAX_FRAME_DELTA_MS = 100;

const MAP_DEFAULT_STATE = Object.freeze({
    center: [47.5, 80.5],
    zoom: 3,
    controls: [],
    behaviors: ["drag", "dblClickZoom", "multiTouch"],
});

const VEHICLE_OPTIONS = Object.freeze({
    iconLayout: "default#image",
    iconImageHref: "/img/landing/decorative-truck-marker.svg",
    iconImageSize: [46, 46],
    iconImageOffset: [-23, -23],
    hasBalloon: false,
    openBalloonOnClick: false,
    hideIconOnBalloonOpen: false,
    zIndex: 550,
});

function useDeferredMapMount(hostRef) {
    const [shouldMount, setShouldMount] = useState(false);

    useEffect(() => {
        if (
            typeof window === "undefined" ||
            !hostRef.current ||
            !("IntersectionObserver" in window)
        ) {
            setShouldMount(true);
            return undefined;
        }

        const observer = new window.IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    return;
                }

                setShouldMount(true);
                observer.disconnect();
            },
            {
                rootMargin: "600px 0px",
                threshold: 0.01,
            }
        );

        observer.observe(hostRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hostRef]);

    return shouldMount;
}

function useMapAnimationVisibility(hostRef) {
    const [isVisible, setVisible] = useState(false);

    useEffect(() => {
        if (prefersReducedMotion()) {
            return undefined;
        }

        if (
            typeof window === "undefined" ||
            !hostRef.current ||
            !("IntersectionObserver" in window)
        ) {
            setVisible(true);
            return undefined;
        }

        const observer = new window.IntersectionObserver(
            ([entry]) => {
                setVisible(entry.isIntersecting);
            },
            {
                rootMargin: "120px 0px",
                threshold: 0.01,
            }
        );

        observer.observe(hostRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hostRef]);

    return isVisible;
}

export default function DecorativeFleetMap({
    ariaLabel,
    balloonLabels = {},
    language,
    vehicleHint,
}) {
    const hostRef = useRef(null);
    const placemarkInstancesRef = useRef({});
    const elapsedMsRef = useRef(0);
    const [selectedVehicleId, setSelectedVehicleId] =
        useState(null);
    const fleet = useMemo(
        () => createDecorativeFleet(),
        []
    );
    const placemarkRefHandlers = useMemo(
        () =>
            fleet.reduce((handlers, vehicle) => {
                handlers[vehicle.id] = (instance) => {
                    if (instance) {
                        placemarkInstancesRef.current[
                            vehicle.id
                        ] = instance;
                        return;
                    }

                    delete placemarkInstancesRef.current[
                        vehicle.id
                    ];
                };

                return handlers;
            }, {}),
        [fleet]
    );
    const shouldMountMap = useDeferredMapMount(hostRef);
    const isAnimationVisible = useMapAnimationVisibility(
        hostRef
    );
    const mapQuery = useMemo(
        () =>
            createYandexMapsQuery(
                language
            ),
        [language]
    );
    const selectedVehicle = fleet.find(
        (vehicle) => vehicle.id === selectedVehicleId
    );
    const selectedVehicleProfile =
        selectedVehicle && selectedVehicle.profile;

    useEffect(() => {
        if (!shouldMountMap || !isAnimationVisible) {
            return undefined;
        }

        let animationFrameId;
        let previousTimestamp = null;
        let previousPaintTimestamp = 0;

        const updateVehiclePositions = () => {
            fleet.forEach((vehicle) => {
                const placemark =
                    placemarkInstancesRef.current[vehicle.id];

                if (!placemark || !placemark.geometry) {
                    return;
                }

                placemark.geometry.setCoordinates(
                    getDecorativeVehiclePosition(
                        vehicle,
                        elapsedMsRef.current
                    )
                );
            });
        };

        const animate = (timestamp) => {
            if (previousTimestamp === null) {
                previousTimestamp = timestamp;
            }

            const frameDelta = Math.min(
                timestamp - previousTimestamp,
                MAX_FRAME_DELTA_MS
            );

            elapsedMsRef.current += Math.max(
                frameDelta,
                0
            );
            previousTimestamp = timestamp;

            if (
                timestamp - previousPaintTimestamp >=
                FRAME_INTERVAL_MS
            ) {
                updateVehiclePositions();
                previousPaintTimestamp = timestamp;
            }

            animationFrameId = window.requestAnimationFrame(
                animate
            );
        };

        animationFrameId = window.requestAnimationFrame(
            animate
        );

        return () => {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [fleet, isAnimationVisible, shouldMountMap]);

    return (
        <div
            ref={hostRef}
            className="decorative-fleet-map"
            role="region"
            aria-label={ariaLabel}
            aria-busy={!shouldMountMap}
        >
            {!shouldMountMap && (
                <div
                    className="decorative-fleet-map__loading"
                    aria-hidden="true"
                />
            )}

            {shouldMountMap && (
                <YMaps query={mapQuery}>
                    <YandexMap
                        className="decorative-fleet-map__canvas"
                        width="100%"
                        height="100%"
                        defaultState={MAP_DEFAULT_STATE}
                        options={{
                            autoFitToViewport: "always",
                            suppressMapOpenBlock: true,
                        }}
                    >
                        {fleet.map((vehicle) => (
                            <Placemark
                                key={vehicle.id}
                                instanceRef={
                                    placemarkRefHandlers[vehicle.id]
                                }
                                defaultGeometry={getDecorativeVehiclePosition(
                                    vehicle,
                                    0
                                )}
                                properties={{
                                    hintContent: vehicleHint,
                                }}
                                onClick={() => {
                                    setSelectedVehicleId(
                                        vehicle.id
                                    );
                                }}
                                options={VEHICLE_OPTIONS}
                                modules={[
                                    "geoObject.addon.hint",
                                ]}
                            />
                        ))}

                        <ZoomControl
                            options={{
                                position: {
                                    top: 20,
                                    right: 20,
                                },
                            }}
                        />
                    </YandexMap>
                </YMaps>
            )}

            {selectedVehicleProfile && (
                <DecorativeFleetVehicleCard
                    profile={
                        selectedVehicleProfile
                    }
                    labels={
                        balloonLabels
                    }
                    onClose={() =>
                        setSelectedVehicleId(
                            null
                        )
                    }
                />
            )}
        </div>
    );
}
